// Marca a sala ABERTA como lida (fix/chat-nao-lidas).
//
// ## O defeito
//
// `useMarkRead`/`ws.markRead` existiam desde `64d1d4b8` sem NENHUM chamador.
// Em produção: 693 linhas em `chat_room_members`, 0 com `last_read_at` — nada
// jamais marcava sala como lida. Este hook é o chamador que faltava.
//
// ## Os três gatilhos (desenho aprovado pelo Augusto)
//
// 1. Abrir uma sala (o `roomId` muda pra um id não-nulo) com a aba visível.
// 2. Voltar à aba (`visibilitychange`) com uma sala já aberta.
// 3. Mensagem nova de OUTRA pessoa chega na sala aberta, aba visível — via
//    `marcarAoChegarMensagem()`, que o `ChatPage` chama a partir do callback
//    `onMessage` do `useChatWebSocket` (mensagem do próprio usuário não conta
//    como não lida no backend, então o `ChatPage` nem chama pra essas).
//
// ## Por que optimistic + invalidate atrasado
//
// `POST /chat/rooms/{id}/read` grava primeiro no Redis; o `event-bus-worker`
// drena pro Postgres a cada ~1s. `GET /chat/rooms` só lê o Postgres — um
// refetch dentro dessa janela devolveria `unread_count` VELHO. Por isso:
// zera o cache na hora (`onMutate`, otimista) e só reconfere com o servidor
// (`invalidateQueries`) depois de passar a janela do worker (`onSettled`,
// com atraso).
import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import chatService from "@/services/chatService";
import type { ChatRoom } from "@/types/chat";
import { isChatRoomsListKey } from "./useChat";

/**
 * Atraso do reinvalidate pós-marcação. Maior que o intervalo real do
 * `event-bus-worker` (~1s) pra sobrar folga de agenda/latência de rede —
 * reinvalidar ANTES disso reintroduziria o `unread_count` velho que o
 * optimistic update de `onMutate` acabou de esconder.
 *
 * Exportado (passo 14): o `NotificationContext` espera o mesmo tempo ao zerar a sala lida
 * noutra aba (`chat:sala_lida`) — a janela do worker é uma só.
 */
export const ATRASO_REINVALIDACAO_MS = 1500;

export interface UseLeituraDaSalaResult {
  /**
   * Chame quando chegar `message:new` de OUTRA pessoa na sala aberta com a
   * aba visível. Sem efeito se não houver sala aberta ou a aba estiver
   * escondida — o próximo `visibilitychange` cobre esse caso.
   */
  marcarAoChegarMensagem: () => void;
}

export function useLeituraDaSala(roomId: string | null): UseLeituraDaSalaResult {
  const qc = useQueryClient();

  // `roomId` mora em ref pro listener de `visibilitychange` (montado uma
  // única vez, ver efeito abaixo) sempre ler a sala ATUAL sem precisar
  // remontar o listener a cada troca de sala.
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;

  // Guarda a sala com marcação em voo (id ou null). Ref, não estado: dois
  // gatilhos quase simultâneos (abrir a sala + mensagem nova chegando logo
  // em seguida, por exemplo) leriam o mesmo `false` de um estado que ainda
  // não recommitou — só uma ref garante a leitura síncrona que evita o POST
  // duplicado.
  const emVooRef = useRef<string | null>(null);
  // Pedido que chegou com a marcação em voo pra MESMA sala. Não dá pra só
  // descartar: o servidor carimba a leitura com o relógio dele na hora do
  // POST, e uma mensagem criada depois desse carimbo ficaria não lida (o
  // badge voltaria ao sair da sala). Vários pedidos durante o voo viram UMA
  // marcação de reforço quando a primeira assenta.
  const reforcoRef = useRef<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limparTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const mutation = useMutation({
    // Sem `lastReadAt`: o servidor usa o próprio relógio — evita relógio
    // torto do cliente (regra do brief; ver `chat.py:1087`).
    mutationFn: (id: string) => chatService.markRead(id),
    onMutate: (id: string) => {
      qc.setQueriesData<ChatRoom[]>({ predicate: isChatRoomsListKey }, (old) =>
        old?.map((sala) =>
          sala.id === id ? { ...sala, unread_count: 0, has_unread_mentions: false } : sala
        )
      );
    },
    // Sem `onError`: falha no POST não pode quebrar a tela (sem toast). O
    // cache já foi zerado otimisticamente em `onMutate` e fica assim — o
    // `onSettled` abaixo roda de qualquer forma (sucesso OU erro) e agenda
    // a reconferência real com o servidor. Não há rollback de propósito:
    // reverter pra `unread_count` antigo faria o badge reaparecer e sumir
    // de novo sozinho, pior que deixar quieto até o invalidate corrigir.
    onSettled: (_data, _err, id) => {
      if (emVooRef.current === id) emVooRef.current = null;
      limparTimer();
      // Só as LISTAS de salas: `{ queryKey: ROOMS_KEY }` é prefixo e casaria
      // as queries escopadas na sala aberta (mensagens, membros), recarregando
      // tudo a cada mensagem recebida.
      timerRef.current = setTimeout(() => {
        qc.invalidateQueries({ predicate: isChatRoomsListKey });
      }, ATRASO_REINVALIDACAO_MS);
      if (reforcoRef.current === id) {
        reforcoRef.current = null;
        marcarRef.current(id);
      }
    },
  });

  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;
  const marcarRef = useRef<(id: string) => void>(() => {});

  // Identidade estável (não depende de `mutation`, que troca de referência a
  // cada render) — assim os efeitos abaixo não precisam recriar listener/
  // reagir a cada render da ChatPage.
  const marcar = useCallback((id: string) => {
    if (emVooRef.current === id) {
      // Já em voo pra essa sala: não duplica agora, reforça quando assentar.
      reforcoRef.current = id;
      return;
    }
    emVooRef.current = id;
    mutateRef.current(id);
  }, []);
  marcarRef.current = marcar;

  // Gatilho 1 — abrir uma sala (id não-nulo) com a aba visível.
  useEffect(() => {
    if (roomId && document.visibilityState === "visible") {
      marcar(roomId);
    }
  }, [roomId, marcar]);

  // Gatilho 2 — voltar à aba com uma sala aberta. Listener montado uma vez;
  // lê a sala atual via `roomIdRef` (ver comentário na declaração da ref).
  useEffect(() => {
    const aoMudarVisibilidade = () => {
      const id = roomIdRef.current;
      if (id && document.visibilityState === "visible") {
        marcar(id);
      }
    };
    document.addEventListener("visibilitychange", aoMudarVisibilidade);
    return () => document.removeEventListener("visibilitychange", aoMudarVisibilidade);
  }, [marcar]);

  // Cancela o reinvalidate agendado se o componente desmontar antes dele
  // disparar (troca de sala rápida, saída da tela) — sem isto o setTimeout
  // sobreviveria ao unmount e invalidaria um QueryClient que ninguém mais lê.
  useEffect(() => () => limparTimer(), [limparTimer]);

  // Gatilho 3 — exposto pro ChatPage chamar na mensagem nova de outra pessoa.
  const marcarAoChegarMensagem = useCallback(() => {
    const id = roomIdRef.current;
    if (id && document.visibilityState === "visible") {
      marcar(id);
    }
  }, [marcar]);

  return { marcarAoChegarMensagem };
}
